import axios, { InternalAxiosRequestConfig } from 'axios';
import { parseISO } from 'date-fns';

import { getApiUrl } from '../utils/query';

// UTC conversion utilities
export const toLocalDate = (date: Date | null | undefined): Date | null | undefined => {
  if (!date) return date;
  const d = new Date(date);
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
};

export const toUTCDate = (date: Date | null | undefined): Date | null => {
  if (!date) return null;
  const d = new Date(date);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000);
};

// Function to convert dates to UTC format for API requests
export const convertDatesToUTC = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;

  if (data instanceof Date) {
    // Convert local date to UTC date string
    const year = data.getFullYear();
    const month = String(data.getMonth() + 1).padStart(2, '0');
    const day = String(data.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }

  if (typeof data === 'object' && !Array.isArray(data)) {
    const converted = {};
    for (const [key, val] of Object.entries(data)) {
      converted[key] = convertDatesToUTC(val);
    }
    return converted;
  }

  if (Array.isArray(data)) {
    return data.map((item) => convertDatesToUTC(item));
  }

  return data;
};

const authRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('accessToken');

  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Convert dates in request data to UTC format
  if (config.data) {
    config.data = convertDatesToUTC(config.data);
  }

  return config;
};

export const ApiClient = axios.create({
  baseURL: getApiUrl().href,
  withCredentials: true,
});

ApiClient.interceptors.request.use(authRequestInterceptor);

ApiClient.interceptors.response.use(
  (response) => {
    response.data = handleDates(response.data);
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    } else {
      const { status, data } = error.response;

      if (status === 400 && data?.message) {
        console.error(error);
        alert(data?.message);
        window.location.href = '/apierror';
        // Business error
        throw new Error(data.message);
      } else if (error.response?.status === 500) {
        console.error(error);
        alert('An unexpected error occurred.');
        // Unexpected server error
        window.location.href = '/apierror';
        throw new Error('An unexpected error occurred. Please try again later.');
      }
      return Promise.reject(error?.response?.data);
    }
  }
);

const ISODateFormat =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

const isIsoDateString = (value: unknown): value is string => {
  return typeof value === 'string' && ISODateFormat.test(value);
};

const handleDates = (data: unknown) => {
  if (isIsoDateString(data)) return parseISO(data);
  if (data === null || data === undefined || typeof data !== 'object') return data;

  for (const [key, val] of Object.entries(data)) {
    if (isIsoDateString(val)) data[key] = parseISO(val);
    else if (typeof val === 'object') handleDates(val);
  }

  return data;
};
